import React, { Fragment, ReactNode } from "react";
import Survey, { useSurveyState } from "./survey";
import DeprecatedButton, { ButtonType } from "../components/data_entry/button";
import { SurveyType } from "../generated/graphql";
import { KIND } from "baseui/button";
import styled from "styled-components";
import { Button } from "../components/base_ui/inputs/button";
import useSurveyValidation from "./shared/validation/use_survey_validation";
import {
  SectionBlock,
  ButtonsContainer as RegistrationButtonsContainer,
} from "../components/base_ui/surfaces/section_block";
import useDeviceType from "../shared/use_device_type";

const SectionBlockOverrides = {
  Root: {
    style: {
      maxWidth: "900px",
    },
  },
  Contents: {
    style: {
      display: "flex",
      flexDirection: "column",
    },
  },
};

const RegistrationFlowSectionBlockOverrides = {
  Root: {
    style: () => ({
      maxWidth: "900px",
      marginLeft: "auto",
      marginRight: "auto",
    }),
  },
  Contents: {
    style: {
      display: "flex",
      flexDirection: "column",
    },
  },
};

// The purpose of this container is to keep old question components
// at a max width of 700px for ease of reading line lengths,
// and to match the new survey component widths while accounting
// for the old components not being surrounded by a SectionBlock or Card.
const DeprecatedStyledSurveyContainer = styled.div`
  max-width: 700px;
`;

const DeprecatedSubmitButtonContainer = styled.div`
  padding-bottom: 0;
  margin: ${({ theme }) => theme.sizing.scale1000} 0px ${({ theme }) => theme.sizing.scale1000}
    ${({ theme }) => theme.sizing.scale300};
  justify-content: start;
  display: flex;
`;

const SubmitButtonContainer = styled.div`
  padding-bottom: 0;
  justify-content: start;
  display: flex;
`;

export interface SurveyAudienceContext {
  mentorshipChatGroupId?: string;
  mentorshipId?: string;
  participantId?: string;
  programId?: string;
}

export default function SurveyPage({
  id,
  userId,
  context,
  introText,
  inRegistrationFlow = false,
  className,
  onSubmit = () => undefined,
}: {
  id: string;
  userId: string;
  context?: SurveyAudienceContext;
  introText?: ReactNode;
  inRegistrationFlow?: boolean;
  className?: string;
  onSubmit?: () => void;
}) {
  const { isMobile } = useDeviceType();
  const surveyState = useSurveyState({
    id,
    onSubmit,
    userId,
    participantId: context?.participantId,
  });

  const {
    fetching,
    survey,
    answers,
    setAnswers,
    savingResponse,
    setIdsOfQuestionsAbleToSubmit,
    setSavingResponse,
    saveSurveyResponse,
    ableToSubmit,
    showValidation,
  } = surveyState;

  const { renderErrorNotification, validateSurvey } = useSurveyValidation(surveyState);

  const QUICK_CONVERSATION_LOGGING_SURVEY_ID = "28";

  // TA-879: due to time and resource constraints, these surveys will not have a refreshed UI
  const showRefreshedComponents =
    survey.surveyType !== SurveyType.PostSession &&
    survey.id !== QUICK_CONVERSATION_LOGGING_SURVEY_ID;

  if (fetching) {
    return <div>...Loading</div>;
  }

  // Buttons are constructed based on whether we're in the registration flow or not
  const Buttons = () => {
    const SubmitButton = ({
      dataAnalyticsId,
      children,
    }: {
      dataAnalyticsId: string;
      children: string;
    }) => (
      <Button
        onClick={async () => {
          try {
            await validateSurvey();
          } catch {
            return;
          }

          setSavingResponse(true);
          await saveSurveyResponse({
            inputResponse: {
              surveyId: id,
              userId,
              contextInput: {
                mentorshipChatGroupId: context?.mentorshipChatGroupId,
                mentorshipId: context?.mentorshipId,
                participantId: context?.participantId,
              },
              answers: Object.keys(answers).map((questionId) => ({
                questionId,
                answerJson: answers[questionId],
              })),
            },
          });
          setSavingResponse(false);
        }}
        disabled={savingResponse}
        data-test="survey-submit"
        data-analytics-id={dataAnalyticsId}
        isStretched={isMobile}
      >
        {children}
      </Button>
    );

    return inRegistrationFlow ? (
      // Buttons for the matching survey in the registration flow
      <RegistrationButtonsContainer>
        <SubmitButton dataAnalyticsId="sign-up-flow-matching-survey-submit-button">
          Submit and Continue
        </SubmitButton>
        <Button
          onClick={onSubmit}
          dataTest="complete-survey-later-button"
          dataAnalyticsId="sign-up-flow-on-matching-survey-later-button"
          kind={KIND.tertiary}
          isStretched={isMobile}
        >
          Complete Survey Later
        </Button>
      </RegistrationButtonsContainer>
    ) : (
      // Buttons for all other surveys
      <SubmitButtonContainer>
        <SubmitButton
          dataAnalyticsId={
            // Not all surveys have types (like the "I can't get in touch" survey, which we track in other ways)
            survey.surveyType ? `survey-page-submit-button-${survey.surveyType}` : ""
          }
        >
          Submit Responses
        </SubmitButton>
      </SubmitButtonContainer>
    );
  };

  const renderSurvey = () => {
    return (
      <>
        {introText}
        <Survey
          answers={answers}
          survey={survey}
          context={context}
          className={className}
          savingResponse={savingResponse}
          setAnswers={setAnswers}
          setIdsOfQuestionsAbleToSubmit={setIdsOfQuestionsAbleToSubmit}
          showValidation={showValidation}
        />
      </>
    );
  };

  return (
    <Fragment>
      {renderErrorNotification(context)}
      {showRefreshedComponents || inRegistrationFlow ? (
        <SectionBlock
          title={inRegistrationFlow ? "Complete Matching Survey" : ""}
          styletronOverrides={
            inRegistrationFlow ? RegistrationFlowSectionBlockOverrides : SectionBlockOverrides
          }
          footer={<Buttons />}
        >
          {renderSurvey()}
        </SectionBlock>
      ) : (
        <DeprecatedStyledSurveyContainer>
          {renderSurvey()}
          <DeprecatedSubmitButtonContainer>
            <DeprecatedButton
              buttonType={ButtonType.primary}
              onClick={async () => {
                setSavingResponse(true);
                await saveSurveyResponse({
                  inputResponse: {
                    surveyId: id,
                    userId,
                    contextInput: {
                      mentorshipChatGroupId: context?.mentorshipChatGroupId,
                      mentorshipId: context?.mentorshipId,
                      participantId: context?.participantId,
                    },
                    answers: Object.keys(answers).map((questionId) => ({
                      questionId,
                      answerJson: answers[questionId],
                    })),
                  },
                });
                setSavingResponse(false);
              }}
              disabled={!ableToSubmit || savingResponse}
              data-test="survey-submit"
              dataAnalyticsId={
                // Not all surveys have types (like the "I can't get in touch" survey, which we track in other ways)
                survey.surveyType ? `survey-page-submit-button-${survey.surveyType}` : undefined
              }
              style={isMobile ? { width: "100%" } : {}}
            >
              Submit
            </DeprecatedButton>
          </DeprecatedSubmitButtonContainer>
        </DeprecatedStyledSurveyContainer>
      )}
    </Fragment>
  );
}
